/**
 * @fileoverview Zod schemas for Baselinker product import API.
 * @module features/baselinker-import/schemas/baselinkerImportSchema
 */

import { z } from 'zod'

/**
 * Baselinker inventory/catalog information.
 */
export const BaselinkerInventorySchema = z.object({
  inventoryId: z.number().int(),
  name: z.string(),
})

export type BaselinkerInventory = z.infer<typeof BaselinkerInventorySchema>

export const BaselinkerInventoryArraySchema = z.array(BaselinkerInventorySchema)

/**
 * Baselinker product variant information.
 */
export const BaselinkerVariantSchema = z.object({
  variantId: z.string(),
  name: z.string(),
  sku: z.string(),
  alreadyExists: z.boolean().optional(),
})

export type BaselinkerVariant = z.infer<typeof BaselinkerVariantSchema>

/**
 * Baselinker product search response.
 */
export const BaselinkerProductSearchResponseSchema = z.object({
  baselinkerProductId: z.string(),
  productName: z.string(),
  sku: z.string(),
  imageUrl: z.string().nullable().optional(),
  cost: z.number().nullable().optional(),
  variants: z.array(BaselinkerVariantSchema),
  alreadyImported: z.boolean(),
})

export type BaselinkerProductSearchResponse = z.infer<typeof BaselinkerProductSearchResponseSchema>

/**
 * Details about a sibling variant conflict - which internal product has which variant SKUs.
 */
export const SiblingConflictDetailSchema = z.object({
  productId: z.number().int(),
  productName: z.string(),
  matchedVariantSkus: z.array(z.string()),
})

export type SiblingConflictDetail = z.infer<typeof SiblingConflictDetailSchema>

/**
 * Product preview item for import.
 */
export const ProductPreviewItemSchema = z.object({
  baselinkerProductId: z.string(),
  productName: z.string(),
  imageUrl: z.string().nullable().optional(),
  sku: z.string().nullable(),
  skuAlreadyExists: z.boolean(),
  variantCount: z.number().int(),
  averageCost: z.number().nullable().optional(),
  variants: z.array(BaselinkerVariantSchema).nullable().optional(),
  /** Whether sibling variants of this product are mapped to different internal products (conflict) */
  siblingConflict: z.boolean().optional(),
  /** Details about the sibling conflict, if any */
  siblingConflictDetails: z.array(SiblingConflictDetailSchema).nullable().optional(),
})

export type ProductPreviewItem = z.infer<typeof ProductPreviewItemSchema>

/**
 * Import preview response.
 */
export const ImportPreviewResponseSchema = z.object({
  totalProducts: z.number().int(),
  alreadyImported: z.number().int(),
  willImport: z.number().int(),
  alreadyImportedProducts: z.array(ProductPreviewItemSchema),
  newProducts: z.array(ProductPreviewItemSchema),
})

export type ImportPreviewResponse = z.infer<typeof ImportPreviewResponseSchema>

/**
 * Request to import products and/or variants from Baselinker catalog.
 * Both productIds and variantSkus can be used together in a single request.
 */
export const BaselinkerImportRequestSchema = z.object({
  inventoryId: z.number().int(),
  productIds: z.array(z.string()).optional(),
  variantSkus: z.array(z.string()).optional(),
})

export type BaselinkerImportRequest = z.infer<typeof BaselinkerImportRequestSchema>

/**
 * Summary of an imported product.
 */
export const ImportedProductSummarySchema = z.object({
  baselinkerProductId: z.string(),
  productName: z.string(),
  dimProductId: z.string().uuid(),
  variantCount: z.number().int(),
})

export type ImportedProductSummary = z.infer<typeof ImportedProductSummarySchema>

/**
 * Details about a failed product import.
 */
export const ImportErrorDetailSchema = z.object({
  baselinkerProductId: z.string(),
  productName: z.string(),
  errorMessage: z.string(),
})

export type ImportErrorDetail = z.infer<typeof ImportErrorDetailSchema>

/**
 * Result of Baselinker product import operation.
 */
export const BaselinkerImportResponseSchema = z.object({
  totalFound: z.number().int(),
  alreadyImported: z.number().int(),
  newlyImported: z.number().int(),
  failed: z.number().int(),
  imported: z.array(ImportedProductSummarySchema),
  errors: z.array(ImportErrorDetailSchema),
})

export type BaselinkerImportResponse = z.infer<typeof BaselinkerImportResponseSchema>
